import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCustomers } from '../context/CustomerContext';
import { format } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import LeadQualifier from '../components/AIFeatures/LeadQualifier';

const { 
  FiArrowLeft, FiMail, FiPhone, FiCar, FiEdit3, FiTrash2, FiPlus, 
  FiCalendar, FiClock, FiDollarSign, FiLoader, FiUserPlus, FiCpu
} = FiIcons;

function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state, addInteraction } = useCustomers();
  const { leads, interactions, loading } = state;
  const lead = leads.find(l => l.id === id);
  const leadInteractions = interactions.filter(i => i.leadId === id);
  const [showAITools, setShowAITools] = useState(false);

  const [newInteraction, setNewInteraction] = useState({
    type: 'call',
    notes: '',
  });

  if (!lead && !loading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Lead not found</p>
        <button
          onClick={() => navigate('/leads')}
          className="mt-4 text-primary-600 hover:text-primary-700 font-medium"
        >
          Back to Leads
        </button>
      </div>
    );
  }

  if (loading || !lead) {
    return (
      <div className="text-center py-12">
        <SafeIcon icon={FiLoader} className="w-8 h-8 text-primary-600 mx-auto mb-4 animate-spin" />
        <p className="text-gray-500">Loading lead details...</p>
      </div>
    );
  }

  const handleAddInteraction = async (e) => {
    e.preventDefault();
    if (!newInteraction.notes.trim()) return;

    try {
      await addInteraction({
        leadId: id,
        type: newInteraction.type,
        notes: newInteraction.notes,
      });
      setNewInteraction({
        type: 'call',
        notes: '',
      });
    } catch (error) {
      console.error('Error adding interaction:', error);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-700';
      case 'contacted': return 'bg-yellow-100 text-yellow-700';
      case 'qualified': return 'bg-green-100 text-green-700';
      case 'converted': return 'bg-purple-100 text-purple-700';
      case 'lost': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/leads')}
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
          Back to Leads
        </button>
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => setShowAITools(!showAITools)} 
            className={`p-2 rounded-lg transition-colors ${
              showAITools 
                ? 'bg-primary-50 text-primary-600' 
                : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50'
            }`}
          >
            <SafeIcon icon={FiCpu} className="w-4 h-4" />
          </button>
          <button className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors">
            <SafeIcon icon={FiEdit3} className="w-4 h-4" />
          </button>
          <Link to="/customers">
            <button className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors">
              <SafeIcon icon={FiUserPlus} className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </div>

      {/* Lead Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-8"
      >
        <div className="flex items-start space-x-6">
          <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center">
            <span className="text-orange-600 font-bold text-2xl">
              {lead.firstName?.charAt(0).toUpperCase()}
              {lead.lastName?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {lead.firstName} {lead.lastName}
            </h1>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(lead.status)}`}>
                {lead.status}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(lead.priority)}`}>
                {lead.priority} priority
              </span>
              {lead.source && (
                <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                  {lead.source}
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="flex items-center text-gray-600">
                <SafeIcon icon={FiMail} className="w-5 h-5 mr-3" />
                <a
                  href={`mailto:${lead.email}`}
                  className="hover:text-primary-600 transition-colors"
                >
                  {lead.email}
                </a>
              </div>
              {lead.phone && (
                <div className="flex items-center text-gray-600">
                  <SafeIcon icon={FiPhone} className="w-5 h-5 mr-3" />
                  <a
                    href={`tel:${lead.phone}`}
                    className="hover:text-primary-600 transition-colors"
                  >
                    {lead.phone}
                  </a>
                </div>
              )}
              {lead.interestedVehicle && (
                <div className="flex items-center text-gray-600">
                  <SafeIcon icon={FiCar} className="w-5 h-5 mr-3" />
                  <span>{lead.interestedVehicle}</span>
                </div>
              )}
              {lead.budget && (
                <div className="flex items-center text-gray-600">
                  <SafeIcon icon={FiDollarSign} className="w-5 h-5 mr-3" />
                  <span>Budget: ${lead.budget.toLocaleString()}</span>
                </div>
              )}
              {lead.followUpDate && (
                <div className="flex items-center text-gray-600">
                  <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-3" />
                  <span>Follow-up: {format(new Date(lead.followUpDate), 'MMM d, yyyy')}</span>
                </div>
              )}
              <div className="flex items-center text-gray-600">
                <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-3" />
                <span>Created: {format(new Date(lead.createdAt), 'MMM d, yyyy')}</span>
              </div>
            </div>
            {lead.tags && lead.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {lead.tags.map(tag => (
                  <span
                    key={tag}
                    className="bg-primary-50 text-primary-700 px-3 py-1 rounded-full text-sm font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
            {lead.notes && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2">Notes</h3>
                <p className="text-gray-700">{lead.notes}</p>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* AI Tools Section */}
      {showAITools && (
        <div>
          <LeadQualifier lead={lead} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Add Interaction */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Log Interaction</h3>
          <form onSubmit={handleAddInteraction} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <select
                value={newInteraction.type}
                onChange={e => setNewInteraction(prev => ({ ...prev, type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={loading}
              >
                <option value="call">Phone Call</option>
                <option value="email">Email</option>
                <option value="meeting">Meeting</option>
                <option value="text">Text Message</option>
                <option value="follow_up">Follow Up</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <textarea
                value={newInteraction.notes}
                onChange={e => setNewInteraction(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="What was discussed? Any follow-up actions needed?"
                required
                disabled={loading}
              />
            </div>
            <motion.button
              type="submit"
              whileHover={{ scale: loading ? 1 : 1.02 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              className="w-full bg-primary-600 text-white py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? (
                <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
              ) : (
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
              )}
              <span>{loading ? 'Logging...' : 'Log Interaction'}</span>
            </motion.button>
          </form>
        </motion.div>

        {/* Interaction History */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Interaction History</h3>
          {leadInteractions.length > 0 ? (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {leadInteractions
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                .map(interaction => (
                  <div key={interaction.id} className="border-l-4 border-primary-200 pl-4 py-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-900 capitalize">
                        {interaction.type.replace('_', ' ')}
                      </span>
                      <div className="flex items-center text-gray-500 text-sm">
                        <SafeIcon icon={FiClock} className="w-3 h-3 mr-1" />
                        {format(new Date(interaction.timestamp), 'MMM d, h:mm a')}
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm">{interaction.notes}</p>
                  </div>
                ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <SafeIcon icon={FiClock} className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No interactions yet</p>
              <p className="text-sm text-gray-400 mt-1">Log your first interaction above</p>
            </div>
          )}
        </motion.div>
      </div>

      {/* Convert to Customer */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-gradient-to-r from-primary-500 to-primary-600 p-6 rounded-xl text-white"
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
          <div>
            <h3 className="text-lg font-semibold">Ready to convert this lead?</h3>
            <p className="text-primary-100 mt-1">
              Turn this lead into a customer to track their journey through the sales process
            </p>
          </div>
          <Link to="/customers">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-primary-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiUserPlus} className="w-4 h-4" />
              <span>Convert to Customer</span>
            </motion.button>
          </Link>
        </div>
      </motion.div>
    </div>
  );
}

export default LeadDetail;